import React, {
    Fragment,
    useLayoutEffect,
    useRef,
    useEffect,
    useState,
} from "react";
import { Swiper } from "swiper";
import {
    Navigation,
    Pagination,
    Autoplay,
    EffectFade,
    EffectCube,
    EffectCoverflow,
    EffectFlip,
    EffectCards,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/effect-cube";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-flip";
import "swiper/css/effect-cards";
import {
    RangeControl,
    SelectControl,
    __experimentalUnitControl as UnitControl,
    Flex,
    FlexItem,
    PanelBody,
    ToggleControl,
    Button,
    TabPanel,
    MediaUpload,
    MediaUploadCheck,
    __experimentalText as Text,
    __experimentalView as View,
} from "@wordpress/components";

import { registerBlockType } from "@wordpress/blocks";
import {
    InspectorControls,
    useBlockProps,
    InnerBlocks,
    BlockContextProvider,
    useInnerBlocksProps,
    MediaUploadCheck as BlockEditorMediaUploadCheck,
    MediaUpload as BlockEditorMediaUpload,
    PanelColorSettings,
} from "@wordpress/block-editor";
import UnitRange from "./components/UnitRange";
import { desktop, mobile, tablet } from "@wordpress/icons";
import ResponsiveRange from "./components/ResponsiveRange";

const ALLOWED_BLOCKS = ["kamikaze/slide-block"];

function SliderBlock({ attributes, setAttributes, clientId, context }) {
    const swiperRef = useRef(null);
    const [swiper, setSwiper] = useState(null);
    const [posts, setPosts] = useState([]);
    const [isManualControl, setIsManualControl] = useState(false);

    // Wartość z RangeControl
    const paginationOffset = attributes.paginationOffset || 0;

    // Jednostka z UnitControl
    const paginationUnit = attributes.paginationUnit || "px";

    const initializeSwiper = () => {
        const navigationWrapper = swiperRef.current;

        // Aktualizacja CSS properties
        swiperRef.current.style.setProperty(
            "--swiper-slides-per-view",
            attributes.slidesPerView
        );
        swiperRef.current.style.setProperty(
            "--swiper-slides-per-view-tablet",
            attributes.slidesPerViewTablet
        );
        swiperRef.current.style.setProperty(
            "--swiper-slides-per-view-mobile",
            attributes.slidesPerViewMobile
        );
        swiperRef.current.style.setProperty(
            "--swiper-space-between",
            `${attributes.spaceBetween}px`
        );

        // Przygotuj moduły w zależności od wybranego efektu
        const modules = [Navigation, Pagination];
        if (attributes.autoplay) modules.push(Autoplay);

        switch (attributes.effect) {
            case "fade":
                modules.push(EffectFade);
                break;
            case "cube":
                modules.push(EffectCube);
                break;
            case "coverflow":
                modules.push(EffectCoverflow);
                break;
            case "flip":
                modules.push(EffectFlip);
                break;
            case "cards":
                modules.push(EffectCards);
                break;
        }

        // Sprawdź czy istnieje już instancja Swiper
        if (navigationWrapper.swiper) {
            // Aktualizuj parametry istniejącego slajdera
            navigationWrapper.swiper.destroy(true, true); // Zniszcz poprzednią instancję
        }

        const swiperConfig = {
            modules, // Używamy przygotowanych modułów
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            slidesPerView:
                attributes.effect === "fade" ? 1 : attributes.slidesPerView, // Dla efektu fade zawsze 1 slajd
            spaceBetween: attributes.spaceBetween,
            effect: attributes.effect,
            direction: attributes.direction,
            speed: attributes.speed,
            loop: attributes.loop,
            autoplay: attributes.autoplay
                ? {
                      delay: attributes.autoplayDelay,
                      disableOnInteraction: attributes.pauseOnHover,
                      pauseOnMouseEnter: attributes.pauseOnHover,
                  }
                : false,
            navigation: {
                enabled: true,
                nextEl: `.swiper-button-next-${clientId}`,
                prevEl: `.swiper-button-prev-${clientId}`,
                disabledClass: "swiper-button-disabled",
                hiddenClass: "swiper-button-hidden",
            },
            pagination: {
                el: `.swiper-pagination-${clientId}`,
                clickable: true,
                enabled: true,
                type: attributes.paginationStyle,
                renderProgressbar: function (progressbarFillClass) {
                    return '<span class="' + progressbarFillClass + '"></span>';
                },
                renderBullet: function (index, className) {
                    return '<span class="' + className + '"></span>';
                },
            },
            allowTouchMove: false,
            on: {
                init: function (swiper) {
                    // Dodajemy bezpośrednie nasłuchiwanie po inicjalizacji
                    const nextBtn = navigationWrapper.querySelector(
                        `.swiper-button-next-${clientId}`
                    );
                    const prevBtn = navigationWrapper.querySelector(
                        `.swiper-button-prev-${clientId}`
                    );

                    if (nextBtn) {
                        nextBtn.addEventListener("click", (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            swiper.slideNext();
                        });
                    }

                    if (prevBtn) {
                        prevBtn.addEventListener("click", (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            swiper.slidePrev();
                        });
                    }
                },
            },
        };

        // Dodaj specyficzne ustawienia dla efektów
        if (attributes.effect === "fade") {
            swiperConfig.fadeEffect = {
                crossFade: true,
            };
        } else if (attributes.effect === "coverflow") {
            swiperConfig.coverflowEffect = {
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
            };
        } else if (attributes.effect === "cube") {
            swiperConfig.cubeEffect = {
                shadow: true,
                slideShadows: true,
                shadowOffset: 20,
                shadowScale: 0.94,
            };
        }

        const swiperInstance = new Swiper(navigationWrapper, swiperConfig);
        navigationWrapper.swiper = swiperInstance;
        setSwiper(swiperInstance);

        return () => {
            swiperInstance.destroy(true, true);
        };
    };

    const handlePosts = () => {
        const coreSelect = wp.data.select("core");
        if (!coreSelect) {
            return;
        }

        setAttributes({
            sliderType: "posts",
            isPostSlider: true,
        });

        setTimeout(() => {
            wp.data
                .resolveSelect("core")
                .getEntityRecords("postType", attributes.postType, {
                    per_page: attributes.postsPerPage,
                    status: "publish",
                    orderby: attributes.orderBy,
                    order: attributes.order,
                    categories: attributes.categories.length
                        ? attributes.categories
                        : undefined,
                    tags: attributes.tags.length ? attributes.tags : undefined,
                    exclude: attributes.excludePosts.length
                        ? attributes.excludePosts
                        : undefined,
                })
                .then((fetchedPosts) => {
                    if (fetchedPosts) {
                        setPosts(fetchedPosts);
                        const { replaceInnerBlocks } =
                            wp.data.dispatch("core/block-editor");
                        const newBlocks = Array(fetchedPosts.length)
                            .fill(null)
                            .map((_, index) => {
                                const post = fetchedPosts[index];
                                return wp.blocks.createBlock(
                                    "kamikaze/slide-block",
                                    {
                                        showTitle: true,
                                        showExcerpt: true,
                                        postId: post ? post.id : null,
                                    }
                                );
                            });

                        replaceInnerBlocks(clientId, newBlocks);
                        wp.data
                            .dispatch("core/block-editor")
                            .selectBlock(clientId);

                        initializeSwiper();
                    }
                });
        }, 0);
    };

    const handleStaticSlider = () => {
        setAttributes({
            sliderType: "static",
            isPostSlider: false,
        });

        setTimeout(() => {
            const { replaceInnerBlocks } =
                wp.data.dispatch("core/block-editor");
            const newBlocks = Array(3)
                .fill(null)
                .map(() => wp.blocks.createBlock("kamikaze/slide-block"));

            replaceInnerBlocks(clientId, newBlocks);
            wp.data.dispatch("core/block-editor").selectBlock(clientId);

            initializeSwiper();
        }, 0);
    };

    useEffect(() => {
        if (
            posts.length > 0 &&
            !isManualControl &&
            attributes.totalSlides !== posts.length
        ) {
            setAttributes({ totalSlides: posts.length });
        }
    }, [posts.length, isManualControl]);

    useLayoutEffect(() => {
        initializeSwiper();
    }, [
        clientId,
        attributes.slidesPerView,
        attributes.slidesPerViewTablet,
        attributes.slidesPerViewMobile,
        attributes.spaceBetween,
        attributes.spaceBetweenTablet,
        attributes.spaceBetweenMobile,
        attributes.showNavigation,
        attributes.showNavigationTablet,
        attributes.showNavigationMobile,
        attributes.showPagination,
        attributes.showPaginationTablet,
        attributes.showPaginationMobile,
        attributes.paginationStyle,
        attributes.effect,
        attributes.direction,
        attributes.speed,
        attributes.loop,
        attributes.autoplay,
        attributes.autoplayDelay,
        attributes.pauseOnHover,
    ]);

    const handleTotalSlidesChange = (value) => {
        const minSlides = posts.length || 1;
        const validValue = Math.max(value, minSlides);
        setAttributes({ totalSlides: validValue });
    };

    const blockProps = useBlockProps({
        className: `swiper block-${clientId}`,
    });

    const innerBlocksProps = useInnerBlocksProps(
        {
            className: "swiper-wrapper",
        },
        {
            allowedBlocks: ALLOWED_BLOCKS,
        }
    );

    useEffect(() => {
        if (!attributes.blockId) {
            setAttributes({ blockId: clientId });
        }
    }, []);

    const updateValue = (name, device, direction, value, unit, isComplete) => {
        const attributeName = `${name}${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;

        setAttributes({
            [attributeName]: {
                value: value,
                valueUnit: unit,
            },
        });
    };

    const onReset = (name, device) => {
        const attributeName = `${name}${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;

        if (name === "navigationSize") {
            const defaultValues = {
                desktop: { value: 40, valueUnit: "px" },
                tablet: { value: 35, valueUnit: "px" },
                mobile: { value: 30, valueUnit: "px" },
            };

            setAttributes({
                [attributeName]: defaultValues[device],
            });
        }
    };

    if (!attributes.sliderType) {
        return (
            <div className="components-placeholder is-large">
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        inset: "0px",
                        pointerEvents: "none",
                        opacity: 0,
                        overflow: "hidden",
                        zIndex: -1,
                    }}
                />
                <div className="components-placeholder__label">
                    <svg
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path d="M18 16V4H6v12h12m0-14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h12m-8.5 6a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3M12 14l1.25-2.5 2.5 5H8.5l2-2.5L12 14Z" />
                    </svg>
                    Kami Slider
                </div>
                <div className="components-placeholder__instructions">
                    Wybierz typ slidera, aby rozpocząć
                </div>
                <div className="components-placeholder__fieldset">
                    <Button
                        className="is-next-40px-default-size is-primary"
                        onClick={() => handlePosts()}
                    >
                        Slider z postów
                    </Button>
                    <Button
                        className="is-next-40px-default-size is-secondary"
                        onClick={() => handleStaticSlider()}
                    >
                        Slider statyczny
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <InspectorControls>
                <PanelBody title="Ustawienia ogólne" initialOpen={true}>
                    <Button
                        isSecondary
                        onClick={() => {
                            setAttributes({
                                sliderType: "",
                                isPostSlider: false,
                            });
                        }}
                        style={{
                            width: "100%",
                            marginTop: "0.5vw",
                            marginBottom: "2vw",
                        }}
                    >
                        Zmień typ slidera
                    </Button>
                    <RangeControl
                        label="Ilość slajdów w slajderze"
                        value={attributes.totalSlides}
                        onChange={handleTotalSlidesChange}
                        min={posts.length || 1}
                        max={20}
                        disabled={posts.length > 0 && !isManualControl}
                        help={
                            posts.length > 0
                                ? `Minimalna liczba slajdów: ${posts.length} (liczba dostępnych postów)`
                                : ""
                        }
                        __nextHasNoMarginBottom={true}
                    />
                    {posts.length > 0 && (
                        <ToggleControl
                            label="Dodaj dodatkowe slajdy"
                            checked={isManualControl}
                            onChange={setIsManualControl}
                            __nextHasNoMarginBottom={true}
                        />
                    )}

                    <View
                        style={{
                            display: "block",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                        }}
                    >
                        <TabPanel
                            className="custom-settings-tab spacing-device-tabs"
                            activeClass="active-tab"
                            tabs={[
                                {
                                    name: "desktop",
                                    title: "Desktop",
                                    icon: desktop,
                                },
                                {
                                    name: "tablet",
                                    title: "Tablet",
                                    icon: tablet,
                                },
                                {
                                    name: "mobile",
                                    title: "Mobile",
                                    icon: mobile,
                                },
                            ]}
                        >
                            {(tab) => (
                                <>
                                    <Text
                                        variant="subtitle"
                                        as="h3"
                                        className="tab-title"
                                    ></Text>
                                    <View style={{ padding: "30px 0 30px 0" }}>
                                        {tab.name === "desktop" && (
                                            <>
                                                <RangeControl
                                                    label="Ilość slajdów na ekranie (Desktop)"
                                                    value={
                                                        attributes.slidesPerView
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            slidesPerView:
                                                                value,
                                                        })
                                                    }
                                                    min={1}
                                                    max={6}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                                <RangeControl
                                                    label="Odstęp między slajdami (Desktop)"
                                                    value={
                                                        attributes.spaceBetween
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            spaceBetween: value,
                                                        })
                                                    }
                                                    min={0}
                                                    max={100}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                        {tab.name === "tablet" && (
                                            <>
                                                <RangeControl
                                                    label="Ilość slajdów na ekranie (Tablet)"
                                                    value={
                                                        attributes.slidesPerViewTablet
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            slidesPerViewTablet:
                                                                value,
                                                        })
                                                    }
                                                    min={1}
                                                    max={4}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                                <RangeControl
                                                    label="Odstęp między slajdami (Tablet)"
                                                    value={
                                                        attributes.spaceBetweenTablet
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            spaceBetweenTablet:
                                                                value,
                                                        })
                                                    }
                                                    min={0}
                                                    max={100}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                        {tab.name === "mobile" && (
                                            <>
                                                <RangeControl
                                                    label="Ilość slajdów na ekranie (Mobile)"
                                                    value={
                                                        attributes.slidesPerViewMobile
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            slidesPerViewMobile:
                                                                value,
                                                        })
                                                    }
                                                    min={1}
                                                    max={2}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                                <RangeControl
                                                    label="Odstęp między slajdami (Mobile)"
                                                    value={
                                                        attributes.spaceBetweenMobile
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            spaceBetweenMobile:
                                                                value,
                                                        })
                                                    }
                                                    min={0}
                                                    max={100}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                    </View>
                                </>
                            )}
                        </TabPanel>
                    </View>

                    <SelectControl
                        label="Efekt przejścia"
                        value={attributes.effect}
                        options={[
                            { label: "Slide", value: "slide" },
                            { label: "Fade", value: "fade" },
                            { label: "Cube", value: "cube" },
                            { label: "Coverflow", value: "coverflow" },
                            { label: "Flip", value: "flip" },
                            { label: "Cards", value: "cards" },
                        ]}
                        onChange={(value) => setAttributes({ effect: value })}
                    />

                    <ToggleControl
                        label="Autoplay"
                        checked={attributes.autoplay}
                        onChange={(value) => setAttributes({ autoplay: value })}
                    />

                    {attributes.autoplay && (
                        <>
                            <RangeControl
                                label="Czas między slajdami (ms)"
                                value={attributes.autoplayDelay}
                                onChange={(value) =>
                                    setAttributes({ autoplayDelay: value })
                                }
                                min={1000}
                                max={10000}
                                step={100}
                            />

                            <ToggleControl
                                label="Zatrzymaj przy hover"
                                checked={attributes.pauseOnHover}
                                onChange={(value) =>
                                    setAttributes({ pauseOnHover: value })
                                }
                            />

                            <ToggleControl
                                label="Zapętlaj"
                                checked={attributes.loop}
                                onChange={(value) =>
                                    setAttributes({ loop: value })
                                }
                            />
                        </>
                    )}

                    <SelectControl
                        label="Kierunek"
                        value={attributes.direction}
                        options={[
                            { label: "Poziomy", value: "horizontal" },
                            { label: "Pionowy", value: "vertical" },
                        ]}
                        onChange={(value) =>
                            setAttributes({ direction: value })
                        }
                    />

                    <RangeControl
                        label="Prędkość przejścia (ms)"
                        value={attributes.speed}
                        onChange={(value) => setAttributes({ speed: value })}
                        min={100}
                        max={2000}
                        step={100}
                    />
                </PanelBody>

                <PanelBody title="Nawigacja" initialOpen={false}>
                    <View
                        style={{
                            marginTop: "30px",
                            marginBottom: "10px",
                        }}
                    >
                        <SelectControl
                            label="Styl nawigacji"
                            value={attributes.navigationStyle}
                            options={[
                                { label: "Klasyczny", value: "classic" },
                                { label: "Dolny", value: "bottom" },
                            ]}
                            onChange={(value) =>
                                setAttributes({ navigationStyle: value })
                            }
                        />
                    </View>

                    <View
                        style={{
                            display: "block",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                        }}
                    >
                        <TabPanel
                            className="custom-settings-tab spacing-device-tabs"
                            activeClass="active-tab"
                            tabs={[
                                {
                                    name: "desktop",
                                    title: "Desktop",
                                    icon: desktop,
                                },
                                {
                                    name: "tablet",
                                    title: "Tablet",
                                    icon: tablet,
                                },
                                {
                                    name: "mobile",
                                    title: "Mobile",
                                    icon: mobile,
                                },
                            ]}
                        >
                            {(tab) => (
                                <>
                                    <Text
                                        variant="subtitle"
                                        as="h3"
                                        className="tab-title"
                                    ></Text>
                                    <View style={{ padding: "30px 0 30px 0" }}>
                                        {tab.name === "desktop" && (
                                            <>
                                                <ToggleControl
                                                    label="Pokaż nawigację (Desktop)"
                                                    checked={
                                                        attributes.showNavigation
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            showNavigation:
                                                                value
                                                                    ? true
                                                                    : false,
                                                        })
                                                    }
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                        {tab.name === "tablet" && (
                                            <>
                                                <ToggleControl
                                                    label="Pokaż nawigację (Tablet)"
                                                    checked={
                                                        attributes.showNavigationTablet
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            showNavigationTablet:
                                                                value
                                                                    ? true
                                                                    : false,
                                                        })
                                                    }
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                        {tab.name === "mobile" && (
                                            <>
                                                <ToggleControl
                                                    label="Pokaż nawigację (Mobile)"
                                                    checked={
                                                        attributes.showNavigationMobile
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            showNavigationMobile:
                                                                value
                                                                    ? true
                                                                    : false,
                                                        })
                                                    }
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                />
                                            </>
                                        )}
                                    </View>
                                </>
                            )}
                        </TabPanel>
                    </View>

                    <View style={{ marginBottom: "20px" }}>
                        <ResponsiveRange
                            type="single"
                            name="navigationSize"
                            label="Wielkość strzałek"
                            attributes={{
                                navigationSizeDesktop:
                                    attributes.navigationSizeDesktop,
                                navigationSizeTablet:
                                    attributes.navigationSizeTablet,
                                navigationSizeMobile:
                                    attributes.navigationSizeMobile,
                            }}
                            updateValue={updateValue}
                            onReset={onReset}
                            min={20}
                            max={100}
                        />
                    </View>

                    <PanelColorSettings
                        title="Kolory nawigacji"
                        initialOpen={true}
                        colorSettings={[
                            {
                                value: attributes.navigationBackgroundColor,
                                onChange: (color) =>
                                    setAttributes({
                                        navigationBackgroundColor: color,
                                    }),
                                label: "Kolor tła strzałek",
                            },
                            {
                                value: attributes.navigationArrowColor,
                                onChange: (color) =>
                                    setAttributes({
                                        navigationArrowColor: color,
                                    }),
                                label: "Kolor strzałek",
                            },
                            {
                                value: attributes.navigationBorderColor,
                                onChange: (color) =>
                                    setAttributes({
                                        navigationBorderColor: color,
                                    }),
                                label: "Kolor obramowania",
                            },
                        ]}
                    />

                    {attributes.navigationStyle === "classic" && (
                        <>
                            <UnitRange
                                label="Przesunięcie lewej strzałki"
                                rangeValue={
                                    attributes.navigationArrowOffsetLeft
                                }
                                onRangeChange={(value) =>
                                    setAttributes({
                                        navigationArrowOffsetLeft: value,
                                    })
                                }
                                unitValue={{
                                    value: attributes.navigationArrowOffsetLeft,
                                    unit: attributes.navigationArrowOffsetLeftUnit,
                                }}
                                onUnitChange={({ value, unit }) =>
                                    setAttributes({
                                        navigationArrowOffsetLeft: value,
                                        navigationArrowOffsetLeftUnit: unit,
                                    })
                                }
                                min={-200}
                                max={200}
                            />
                            <UnitRange
                                label="Przesunięcie prawej strzałki"
                                rangeValue={
                                    attributes.navigationArrowOffsetRight
                                }
                                onRangeChange={(value) =>
                                    setAttributes({
                                        navigationArrowOffsetRight: value,
                                    })
                                }
                                unitValue={{
                                    value: attributes.navigationArrowOffsetRight,
                                    unit: attributes.navigationArrowOffsetRightUnit,
                                }}
                                onUnitChange={({ value, unit }) =>
                                    setAttributes({
                                        navigationArrowOffsetRight: value,
                                        navigationArrowOffsetRightUnit: unit,
                                    })
                                }
                                min={-200}
                                max={200}
                            />
                        </>
                    )}
                </PanelBody>

                <PanelBody title="Paginacja" initialOpen={false}>
                    <View
                        style={{
                            marginTop: "30px",
                            marginBottom: "10px",
                        }}
                    >
                        <SelectControl
                            label="Styl paginacji"
                            value={attributes.paginationStyle}
                            options={[
                                { label: "Bullets", value: "bullets" },
                                {
                                    label: "Modern bullets",
                                    value: "modern-bullets",
                                },
                            ]}
                            onChange={(value) =>
                                setAttributes({ paginationStyle: value })
                            }
                        />
                    </View>

                    <SelectControl
                        label="Pozycja paginacji"
                        value={attributes.paginationPosition}
                        options={[
                            { label: "Środek", value: "center" },
                            { label: "Lewo", value: "left" },
                            { label: "Prawo", value: "right" },
                        ]}
                        onChange={(value) =>
                            setAttributes({ paginationPosition: value })
                        }
                    />

                    <UnitRange
                        label="Przesunięcie paginacji"
                        rangeValue={attributes.paginationOffset}
                        onRangeChange={(value) =>
                            setAttributes({ paginationOffset: value })
                        }
                        unitValue={{
                            value: attributes.paginationOffset,
                            unit: attributes.paginationUnit,
                        }}
                        onUnitChange={({ value, unit }) =>
                            setAttributes({
                                paginationOffset: value,
                                paginationUnit: unit,
                            })
                        }
                        min={-100}
                        max={100}
                    />

                    <View
                        style={{
                            display: "block",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                        }}
                    >
                        <TabPanel
                            className="custom-settings-tab spacing-device-tabs"
                            activeClass="active-tab"
                            tabs={[
                                {
                                    name: "desktop",
                                    title: "Desktop",
                                    icon: desktop,
                                },
                                {
                                    name: "tablet",
                                    title: "Tablet",
                                    icon: tablet,
                                },
                                {
                                    name: "mobile",
                                    title: "Mobile",
                                    icon: mobile,
                                },
                            ]}
                        >
                            {(tab) => (
                                <>
                                    <Text
                                        variant="subtitle"
                                        as="h3"
                                        className="tab-title"
                                    ></Text>
                                    <View style={{ padding: "30px 0 30px 0" }}>
                                        {tab.name === "desktop" && (
                                            <ToggleControl
                                                label="Pokaż paginację (Desktop)"
                                                checked={
                                                    attributes.showPagination
                                                }
                                                onChange={(value) =>
                                                    setAttributes({
                                                        showPagination: value
                                                            ? true
                                                            : false,
                                                    })
                                                }
                                                __nextHasNoMarginBottom={true}
                                            />
                                        )}
                                        {tab.name === "tablet" && (
                                            <ToggleControl
                                                label="Pokaż paginację (Tablet)"
                                                checked={
                                                    attributes.showPaginationTablet
                                                }
                                                onChange={(value) =>
                                                    setAttributes({
                                                        showPaginationTablet:
                                                            value
                                                                ? true
                                                                : false,
                                                    })
                                                }
                                                __nextHasNoMarginBottom={true}
                                            />
                                        )}
                                        {tab.name === "mobile" && (
                                            <ToggleControl
                                                label="Pokaż paginację (Mobile)"
                                                checked={
                                                    attributes.showPaginationMobile
                                                }
                                                onChange={(value) =>
                                                    setAttributes({
                                                        showPaginationMobile:
                                                            value
                                                                ? true
                                                                : false,
                                                    })
                                                }
                                                __nextHasNoMarginBottom={true}
                                            />
                                        )}
                                    </View>
                                </>
                            )}
                        </TabPanel>
                    </View>
                </PanelBody>

                <PanelBody title="Overlay tła" initialOpen={false}>
                    <View
                        style={{
                            display: "block",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                        }}
                    >
                        <TabPanel
                            className="custom-settings-tab spacing-device-tabs"
                            activeClass="active-tab"
                            tabs={[
                                {
                                    name: "desktop",
                                    title: "Desktop",
                                    icon: desktop,
                                },
                                {
                                    name: "tablet",
                                    title: "Tablet",
                                    icon: tablet,
                                },
                                {
                                    name: "mobile",
                                    title: "Mobile",
                                    icon: mobile,
                                },
                            ]}
                        >
                            {(tab) => {
                                const isDesktop = tab.name === "desktop";
                                const isTablet = tab.name === "tablet";
                                const isMobile = tab.name === "mobile";

                                const suffix = isDesktop
                                    ? ""
                                    : isTablet
                                    ? "Tablet"
                                    : "Mobile";

                                const currentImage =
                                    attributes[`overlayImage${suffix}`];
                                const currentPosition =
                                    attributes[`overlayPosition${suffix}`];
                                const currentSize =
                                    attributes[`overlaySize${suffix}`];
                                const currentSizeUnit =
                                    attributes[`overlaySizeUnit${suffix}`];
                                const currentOpacity =
                                    attributes[`overlayOpacity${suffix}`];
                                const currentBackgroundSize =
                                    attributes[
                                        `overlayBackgroundSize${suffix}`
                                    ];
                                const currentBackgroundPosition =
                                    attributes[
                                        `overlayBackgroundPosition${suffix}`
                                    ];
                                const currentBackgroundRepeat =
                                    attributes[
                                        `overlayBackgroundRepeat${suffix}`
                                    ];
                                const currentZIndex =
                                    attributes[`overlayZIndex${suffix}`];
                                const currentBlendMode =
                                    attributes[`overlayBlendMode${suffix}`];
                                const currentRotate =
                                    attributes[`overlayRotate${suffix}`];
                                const currentScaleX =
                                    attributes[`overlayScaleX${suffix}`];
                                const currentScaleY =
                                    attributes[`overlayScaleY${suffix}`];
                                const currentTranslateX =
                                    attributes[`overlayTranslateX${suffix}`];
                                const currentTranslateY =
                                    attributes[`overlayTranslateY${suffix}`];
                                const currentTranslateXUnit =
                                    attributes[
                                        `overlayTranslateXUnit${suffix}`
                                    ];
                                const currentTranslateYUnit =
                                    attributes[
                                        `overlayTranslateYUnit${suffix}`
                                    ];

                                return (
                                    <>
                                        <Text
                                            variant="subtitle"
                                            as="h3"
                                            className="tab-title"
                                        ></Text>
                                        <View
                                            style={{ padding: "30px 0 30px 0" }}
                                        >
                                            <BlockEditorMediaUploadCheck>
                                                <BlockEditorMediaUpload
                                                    onSelect={(media) => {
                                                        setAttributes({
                                                            [`overlayImage${suffix}`]:
                                                                media.url,
                                                        });
                                                    }}
                                                    allowedTypes={["image"]}
                                                    value={currentImage}
                                                    render={({ open }) => (
                                                        <Button
                                                            onClick={open}
                                                            variant="secondary"
                                                            style={{
                                                                marginBottom:
                                                                    "10px",
                                                                width: "100%",
                                                            }}
                                                        >
                                                            {currentImage
                                                                ? `Zmień obraz overlay (${tab.title})`
                                                                : `Wybierz obraz overlay (${tab.title})`}
                                                        </Button>
                                                    )}
                                                />
                                            </BlockEditorMediaUploadCheck>

                                            {currentImage && (
                                                <>
                                                    <Button
                                                        onClick={() =>
                                                            setAttributes({
                                                                [`overlayImage${suffix}`]:
                                                                    "",
                                                            })
                                                        }
                                                        variant="link"
                                                        isDestructive
                                                        style={{
                                                            marginBottom:
                                                                "15px",
                                                        }}
                                                    >
                                                        Usuń obraz
                                                    </Button>

                                                    <SelectControl
                                                        label={`Pozycja overlay (${tab.title})`}
                                                        value={currentPosition}
                                                        options={[
                                                            {
                                                                label: "Góra",
                                                                value: "top",
                                                            },
                                                            {
                                                                label: "Dół",
                                                                value: "bottom",
                                                            },
                                                        ]}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayPosition${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                    />

                                                    <UnitRange
                                                        label={`Rozmiar overlay (${tab.title})`}
                                                        rangeValue={currentSize}
                                                        onRangeChange={(
                                                            value
                                                        ) =>
                                                            setAttributes({
                                                                [`overlaySize${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        unitValue={{
                                                            value: currentSize,
                                                            unit: currentSizeUnit,
                                                        }}
                                                        onUnitChange={({
                                                            value,
                                                            unit,
                                                        }) =>
                                                            setAttributes({
                                                                [`overlaySize${suffix}`]:
                                                                    value,
                                                                [`overlaySizeUnit${suffix}`]:
                                                                    unit,
                                                            })
                                                        }
                                                        min={0}
                                                        max={1000}
                                                    />

                                                    <SelectControl
                                                        label={`Rozmiar tła (${tab.title})`}
                                                        value={
                                                            currentBackgroundSize
                                                        }
                                                        options={[
                                                            {
                                                                label: "Cover",
                                                                value: "cover",
                                                            },
                                                            {
                                                                label: "Contain",
                                                                value: "contain",
                                                            },
                                                            {
                                                                label: "Auto",
                                                                value: "auto",
                                                            },
                                                            {
                                                                label: "100%",
                                                                value: "100%",
                                                            },
                                                        ]}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayBackgroundSize${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                    />

                                                    <SelectControl
                                                        label={`Pozycja tła (${tab.title})`}
                                                        value={
                                                            currentBackgroundPosition
                                                        }
                                                        options={[
                                                            {
                                                                label: "Środek",
                                                                value: "center center",
                                                            },
                                                            {
                                                                label: "Góra",
                                                                value: "top center",
                                                            },
                                                            {
                                                                label: "Dół",
                                                                value: "bottom center",
                                                            },
                                                            {
                                                                label: "Lewo",
                                                                value: "center left",
                                                            },
                                                            {
                                                                label: "Prawo",
                                                                value: "center right",
                                                            },
                                                            {
                                                                label: "Góra lewo",
                                                                value: "top left",
                                                            },
                                                            {
                                                                label: "Góra prawo",
                                                                value: "top right",
                                                            },
                                                            {
                                                                label: "Dół lewo",
                                                                value: "bottom left",
                                                            },
                                                            {
                                                                label: "Dół prawo",
                                                                value: "bottom right",
                                                            },
                                                        ]}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayBackgroundPosition${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                    />

                                                    <SelectControl
                                                        label={`Powtarzanie tła (${tab.title})`}
                                                        value={
                                                            currentBackgroundRepeat
                                                        }
                                                        options={[
                                                            {
                                                                label: "Bez powtarzania",
                                                                value: "no-repeat",
                                                            },
                                                            {
                                                                label: "Powtarzaj",
                                                                value: "repeat",
                                                            },
                                                            {
                                                                label: "Powtarzaj X",
                                                                value: "repeat-x",
                                                            },
                                                            {
                                                                label: "Powtarzaj Y",
                                                                value: "repeat-y",
                                                            },
                                                        ]}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayBackgroundRepeat${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                    />

                                                    <RangeControl
                                                        label={`Przezroczystość (${tab.title})`}
                                                        value={currentOpacity}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayOpacity${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        min={0}
                                                        max={1}
                                                        step={0.1}
                                                    />

                                                    <RangeControl
                                                        label={`Z-Index (${tab.title})`}
                                                        value={currentZIndex}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayZIndex${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        min={-10}
                                                        max={10}
                                                        step={1}
                                                    />

                                                    <SelectControl
                                                        label={`Tryb mieszania (${tab.title})`}
                                                        value={currentBlendMode}
                                                        options={[
                                                            {
                                                                label: "Normal",
                                                                value: "normal",
                                                            },
                                                            {
                                                                label: "Multiply",
                                                                value: "multiply",
                                                            },
                                                            {
                                                                label: "Screen",
                                                                value: "screen",
                                                            },
                                                            {
                                                                label: "Overlay",
                                                                value: "overlay",
                                                            },
                                                            {
                                                                label: "Darken",
                                                                value: "darken",
                                                            },
                                                            {
                                                                label: "Lighten",
                                                                value: "lighten",
                                                            },
                                                            {
                                                                label: "Color Dodge",
                                                                value: "color-dodge",
                                                            },
                                                            {
                                                                label: "Color Burn",
                                                                value: "color-burn",
                                                            },
                                                            {
                                                                label: "Hard Light",
                                                                value: "hard-light",
                                                            },
                                                            {
                                                                label: "Soft Light",
                                                                value: "soft-light",
                                                            },
                                                            {
                                                                label: "Difference",
                                                                value: "difference",
                                                            },
                                                            {
                                                                label: "Exclusion",
                                                                value: "exclusion",
                                                            },
                                                        ]}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayBlendMode${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                    />

                                                    <RangeControl
                                                        label={`Obrót (stopnie) (${tab.title})`}
                                                        value={currentRotate}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayRotate${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        min={-180}
                                                        max={180}
                                                        step={1}
                                                    />

                                                    <RangeControl
                                                        label={`Skala X (${tab.title})`}
                                                        value={currentScaleX}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayScaleX${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        min={0.1}
                                                        max={2}
                                                        step={0.1}
                                                    />

                                                    <RangeControl
                                                        label={`Skala Y (${tab.title})`}
                                                        value={currentScaleY}
                                                        onChange={(value) =>
                                                            setAttributes({
                                                                [`overlayScaleY${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        min={0.1}
                                                        max={2}
                                                        step={0.1}
                                                    />

                                                    <UnitRange
                                                        label={`Przesunięcie X (${tab.title})`}
                                                        rangeValue={
                                                            currentTranslateX
                                                        }
                                                        onRangeChange={(
                                                            value
                                                        ) =>
                                                            setAttributes({
                                                                [`overlayTranslateX${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        unitValue={{
                                                            value: currentTranslateX,
                                                            unit: currentTranslateXUnit,
                                                        }}
                                                        onUnitChange={({
                                                            value,
                                                            unit,
                                                        }) =>
                                                            setAttributes({
                                                                [`overlayTranslateX${suffix}`]:
                                                                    value,
                                                                [`overlayTranslateXUnit${suffix}`]:
                                                                    unit,
                                                            })
                                                        }
                                                        min={-500}
                                                        max={500}
                                                    />

                                                    <UnitRange
                                                        label={`Przesunięcie Y (${tab.title})`}
                                                        rangeValue={
                                                            currentTranslateY
                                                        }
                                                        onRangeChange={(
                                                            value
                                                        ) =>
                                                            setAttributes({
                                                                [`overlayTranslateY${suffix}`]:
                                                                    value,
                                                            })
                                                        }
                                                        unitValue={{
                                                            value: currentTranslateY,
                                                            unit: currentTranslateYUnit,
                                                        }}
                                                        onUnitChange={({
                                                            value,
                                                            unit,
                                                        }) =>
                                                            setAttributes({
                                                                [`overlayTranslateY${suffix}`]:
                                                                    value,
                                                                [`overlayTranslateYUnit${suffix}`]:
                                                                    unit,
                                                            })
                                                        }
                                                        min={-500}
                                                        max={500}
                                                    />
                                                </>
                                            )}
                                        </View>
                                    </>
                                );
                            }}
                        </TabPanel>
                    </View>
                </PanelBody>
            </InspectorControls>

            <div {...blockProps} ref={swiperRef}>
                {(attributes.overlayImage ||
                    attributes.overlayImageTablet ||
                    attributes.overlayImageMobile) && (
                    <>
                        {attributes.overlayImage && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPosition} desktop-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImage})`,
                                    height: `${attributes.overlaySize}${attributes.overlaySizeUnit}`,
                                    opacity: attributes.overlayOpacity,
                                    backgroundSize:
                                        attributes.overlayBackgroundSize,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPosition,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeat,
                                    zIndex: attributes.overlayZIndex,
                                    mixBlendMode: attributes.overlayBlendMode,
                                    transform: `
                                        rotate(${attributes.overlayRotate}deg)
                                        scaleX(${attributes.overlayScaleX})
                                        scaleY(${attributes.overlayScaleY})
                                        translateX(${attributes.overlayTranslateX}${attributes.overlayTranslateXUnit})
                                        translateY(${attributes.overlayTranslateY}${attributes.overlayTranslateYUnit})
                                    `,
                                }}
                            />
                        )}
                        {attributes.overlayImageTablet && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPositionTablet} tablet-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImageTablet})`,
                                    height: `${attributes.overlaySizeTablet}${attributes.overlaySizeUnitTablet}`,
                                    opacity: attributes.overlayOpacityTablet,
                                    backgroundSize:
                                        attributes.overlayBackgroundSizeTablet,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPositionTablet,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeatTablet,
                                    zIndex: attributes.overlayZIndexTablet,
                                    mixBlendMode:
                                        attributes.overlayBlendModeTablet,
                                    transform: `
                                        rotate(${attributes.overlayRotateTablet}deg)
                                        scaleX(${attributes.overlayScaleXTablet})
                                        scaleY(${attributes.overlayScaleYTablet})
                                        translateX(${attributes.overlayTranslateXTablet}${attributes.overlayTranslateXUnitTablet})
                                        translateY(${attributes.overlayTranslateYTablet}${attributes.overlayTranslateYUnitTablet})
                                    `,
                                }}
                            />
                        )}
                        {attributes.overlayImageMobile && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPositionMobile} mobile-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImageMobile})`,
                                    height: `${attributes.overlaySizeMobile}${attributes.overlaySizeUnitMobile}`,
                                    opacity: attributes.overlayOpacityMobile,
                                    backgroundSize:
                                        attributes.overlayBackgroundSizeMobile,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPositionMobile,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeatMobile,
                                    zIndex: attributes.overlayZIndexMobile,
                                    mixBlendMode:
                                        attributes.overlayBlendModeMobile,
                                    transform: `
                                        rotate(${attributes.overlayRotateMobile}deg)
                                        scaleX(${attributes.overlayScaleXMobile})
                                        scaleY(${attributes.overlayScaleYMobile})
                                        translateX(${attributes.overlayTranslateXMobile}${attributes.overlayTranslateXUnitMobile})
                                        translateY(${attributes.overlayTranslateYMobile}${attributes.overlayTranslateYUnitMobile})
                                    `,
                                }}
                            />
                        )}
                    </>
                )}
                <div {...innerBlocksProps} />
                <div
                    className={`swiper-navigation swiper-navigation-${attributes.blockId} swiper-navigation-style-${attributes.navigationStyle}`}
                >
                    <div
                        className={`swiper-button-prev swiper-button-prev-${clientId}`}
                        style={{
                            visibility:
                                attributes.showNavigation ||
                                attributes.showNavigationTablet ||
                                attributes.showNavigationMobile
                                    ? "visible"
                                    : "hidden",
                            width: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            height: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            backgroundColor:
                                attributes.navigationBackgroundColor,
                            borderColor: attributes.navigationBorderColor,
                            transform: `translateX(${attributes.navigationArrowOffsetLeft}${attributes.navigationArrowOffsetLeftUnit})`,
                            "--navigation-size-desktop": `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            "--navigation-size-tablet": `${attributes.navigationSizeTablet.value}${attributes.navigationSizeTablet.valueUnit}`,
                            "--navigation-size-mobile": `${attributes.navigationSizeMobile.value}${attributes.navigationSizeMobile.valueUnit}`,
                            "--swiper-navigation-color":
                                attributes.navigationArrowColor,
                        }}
                    />
                    <div
                        className={`swiper-button-next swiper-button-next-${clientId}`}
                        style={{
                            visibility:
                                attributes.showNavigation ||
                                attributes.showNavigationTablet ||
                                attributes.showNavigationMobile
                                    ? "visible"
                                    : "hidden",
                            width: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            height: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            backgroundColor:
                                attributes.navigationBackgroundColor,
                            borderColor: attributes.navigationBorderColor,
                            transform: `translateX(${attributes.navigationArrowOffsetRight}${attributes.navigationArrowOffsetRightUnit})`,
                            "--navigation-size-desktop": `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            "--navigation-size-tablet": `${attributes.navigationSizeTablet.value}${attributes.navigationSizeTablet.valueUnit}`,
                            "--navigation-size-mobile": `${attributes.navigationSizeMobile.value}${attributes.navigationSizeMobile.valueUnit}`,
                            "--swiper-navigation-color":
                                attributes.navigationArrowColor,
                        }}
                    />
                </div>
                <div
                    className={`swiper-pagination swiper-pagination-${attributes.blockId} swiper-pagination-style-${attributes.paginationStyle} swiper-pagination-position-${attributes.paginationPosition}`}
                    data-pagination-style={attributes.paginationStyle}
                    style={{
                        visibility:
                            attributes.showPagination ||
                            attributes.showPaginationTablet ||
                            attributes.showPaginationMobile
                                ? "visible"
                                : "hidden",
                        transform: `translateX(${attributes.paginationOffset}${attributes.paginationUnit})`,
                    }}
                ></div>
            </div>
        </Fragment>
    );
}

registerBlockType("kamikaze/slider-block", {
    apiVersion: 2,
    title: "Kami Slider",
    icon: "slides",
    category: "design",
    attributes: {
        slidesPerView: {
            type: "number",
            default: 3,
        },
        slidesPerViewTablet: {
            type: "number",
            default: 2,
        },
        slidesPerViewMobile: {
            type: "number",
            default: 1,
        },
        totalSlides: {
            type: "number",
            default: 3,
        },
        spaceBetween: {
            type: "number",
            default: 32,
        },
        spaceBetweenTablet: {
            type: "number",
            default: 24,
        },
        spaceBetweenMobile: {
            type: "number",
            default: 16,
        },
        showNavigation: {
            type: "boolean",
            default: true,
        },
        showNavigationTablet: {
            type: "boolean",
            default: true,
        },
        showNavigationMobile: {
            type: "boolean",
            default: true,
        },
        showPagination: {
            type: "boolean",
            default: true,
        },
        showPaginationTablet: {
            type: "boolean",
            default: true,
        },
        showPaginationMobile: {
            type: "boolean",
            default: true,
        },
        isPostSlider: {
            type: "boolean",
            default: false,
        },
        postType: {
            type: "string",
            default: "post",
        },
        postsPerPage: {
            type: "number",
            default: 10,
        },
        orderBy: {
            type: "string",
            default: "date",
        },
        order: {
            type: "string",
            default: "desc",
        },
        categories: {
            type: "array",
            default: [],
        },
        tags: {
            type: "array",
            default: [],
        },
        excludePosts: {
            type: "array",
            default: [],
        },
        sliderType: {
            type: "string",
            default: "",
        },
        blockId: {
            type: "string",
        },
        navigationStyle: {
            type: "string",
            default: "classic",
        },
        navigationArrowHeightDesktop: {
            type: "number",
            default: 50,
        },
        navigationArrowHeightTablet: {
            type: "number",
            default: 50,
        },
        navigationArrowHeightMobile: {
            type: "number",
            default: 50,
        },
        paginationStyle: {
            type: "string",
            default: "bullets",
        },
        paginationPosition: {
            type: "string",
            default: "center",
        },
        paginationOffset: {
            type: "number",
            default: 0,
        },
        paginationUnit: {
            type: "string",
            default: "px",
        },
        overlayImage: {
            type: "string",
            default: "",
        },
        overlayPosition: {
            type: "string",
            default: "top",
        },
        overlaySize: {
            type: "number",
            default: 100,
        },
        overlayOpacity: {
            type: "number",
            default: 1,
        },
        overlayBackgroundSize: {
            type: "string",
            default: "cover",
        },
        overlayBackgroundPosition: {
            type: "string",
            default: "center center",
        },
        overlayBackgroundRepeat: {
            type: "string",
            default: "no-repeat",
        },
        overlayZIndex: {
            type: "number",
            default: 1,
        },
        overlayBlendMode: {
            type: "string",
            default: "normal",
        },
        overlayRotate: {
            type: "number",
            default: 0,
        },
        overlayScaleX: {
            type: "number",
            default: 1,
        },
        overlayScaleY: {
            type: "number",
            default: 1,
        },
        overlayTranslateX: {
            type: "number",
            default: 0,
        },
        overlayTranslateY: {
            type: "number",
            default: 0,
        },
        navigationArrowOffsetLeft: {
            type: "number",
            default: 0,
        },
        navigationArrowOffsetRight: {
            type: "number",
            default: 0,
        },
        navigationArrowOffsetLeftUnit: {
            type: "string",
            default: "px",
        },
        navigationArrowOffsetRightUnit: {
            type: "string",
            default: "px",
        },
        overlaySizeUnit: {
            type: "string",
            default: "%",
        },
        overlayTranslateXUnit: {
            type: "string",
            default: "px",
        },
        overlayTranslateYUnit: {
            type: "string",
            default: "px",
        },
        overlayImageTablet: {
            type: "string",
            default: "",
        },
        overlayPositionTablet: {
            type: "string",
            default: "top",
        },
        overlaySizeTablet: {
            type: "number",
            default: 100,
        },
        overlaySizeUnitTablet: {
            type: "string",
            default: "%",
        },
        overlayOpacityTablet: {
            type: "number",
            default: 1,
        },
        overlayBackgroundSizeTablet: {
            type: "string",
            default: "cover",
        },
        overlayBackgroundPositionTablet: {
            type: "string",
            default: "center center",
        },
        overlayBackgroundRepeatTablet: {
            type: "string",
            default: "no-repeat",
        },
        overlayZIndexTablet: {
            type: "number",
            default: 1,
        },
        overlayBlendModeTablet: {
            type: "string",
            default: "normal",
        },
        overlayRotateTablet: {
            type: "number",
            default: 0,
        },
        overlayScaleXTablet: {
            type: "number",
            default: 1,
        },
        overlayScaleYTablet: {
            type: "number",
            default: 1,
        },
        overlayTranslateXTablet: {
            type: "number",
            default: 0,
        },
        overlayTranslateYTablet: {
            type: "number",
            default: 0,
        },
        overlayTranslateXUnitTablet: {
            type: "string",
            default: "px",
        },
        overlayTranslateYUnitTablet: {
            type: "string",
            default: "px",
        },
        overlayImageMobile: {
            type: "string",
            default: "",
        },
        overlayPositionMobile: {
            type: "string",
            default: "top",
        },
        overlaySizeMobile: {
            type: "number",
            default: 100,
        },
        overlaySizeUnitMobile: {
            type: "string",
            default: "%",
        },
        overlayOpacityMobile: {
            type: "number",
            default: 1,
        },
        overlayBackgroundSizeMobile: {
            type: "string",
            default: "cover",
        },
        overlayBackgroundPositionMobile: {
            type: "string",
            default: "center center",
        },
        overlayBackgroundRepeatMobile: {
            type: "string",
            default: "no-repeat",
        },
        overlayZIndexMobile: {
            type: "number",
            default: 1,
        },
        overlayBlendModeMobile: {
            type: "string",
            default: "normal",
        },
        overlayRotateMobile: {
            type: "number",
            default: 0,
        },
        overlayScaleXMobile: {
            type: "number",
            default: 1,
        },
        overlayScaleYMobile: {
            type: "number",
            default: 1,
        },
        overlayTranslateXMobile: {
            type: "number",
            default: 0,
        },
        overlayTranslateYMobile: {
            type: "number",
            default: 0,
        },
        overlayTranslateXUnitMobile: {
            type: "string",
            default: "px",
        },
        overlayTranslateYUnitMobile: {
            type: "string",
            default: "px",
        },
        effect: {
            type: "string",
            default: "slide",
        },
        autoplay: {
            type: "boolean",
            default: false,
        },
        autoplayDelay: {
            type: "number",
            default: 3000,
        },
        pauseOnHover: {
            type: "boolean",
            default: true,
        },
        loop: {
            type: "boolean",
            default: true,
        },
        direction: {
            type: "string",
            default: "horizontal",
        },
        speed: {
            type: "number",
            default: 300,
        },
        navigationSizeDesktop: {
            type: "object",
            default: {
                value: 40,
                valueUnit: "px",
            },
        },
        navigationSizeTablet: {
            type: "object",
            default: {
                value: 35,
                valueUnit: "px",
            },
        },
        navigationSizeMobile: {
            type: "object",
            default: {
                value: 30,
                valueUnit: "px",
            },
        },
        navigationBackgroundColor: {
            type: "string",
            default: "#ffffff",
        },
        navigationArrowColor: {
            type: "string",
            default: "#000000",
        },
        navigationBorderColor: {
            type: "string",
            default: "rgba(0, 0, 0, 0.1)",
        },
    },
    supports: {
        html: false,
        align: ["wide", "full"],
    },
    edit: SliderBlock,
    save: function Save({ attributes }) {
        const blockProps = useBlockProps.save({
            className: "swiper",
            "data-slides-per-view": attributes.slidesPerView,
            "data-slides-per-view-tablet": attributes.slidesPerViewTablet,
            "data-slides-per-view-mobile": attributes.slidesPerViewMobile,
            "data-space-between": attributes.spaceBetween,
            "data-space-between-tablet": attributes.spaceBetweenTablet,
            "data-space-between-mobile": attributes.spaceBetweenMobile,
            "data-show-navigation": attributes.showNavigation,
            "data-show-navigation-tablet": attributes.showNavigationTablet,
            "data-show-pagination": attributes.showPagination,
            "data-show-pagination-tablet": attributes.showPaginationTablet,
            "data-show-pagination-mobile": attributes.showPaginationMobile,
            "data-navigation-mobile": attributes.showNavigationMobile,
        });

        return (
            <div {...blockProps}>
                {(attributes.overlayImage ||
                    attributes.overlayImageTablet ||
                    attributes.overlayImageMobile) && (
                    <>
                        {attributes.overlayImage && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPosition} desktop-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImage})`,
                                    height: `${attributes.overlaySize}${attributes.overlaySizeUnit}`,
                                    opacity: attributes.overlayOpacity,
                                    backgroundSize:
                                        attributes.overlayBackgroundSize,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPosition,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeat,
                                    zIndex: attributes.overlayZIndex,
                                    mixBlendMode: attributes.overlayBlendMode,
                                    transform: `
                                        rotate(${attributes.overlayRotate}deg)
                                        scaleX(${attributes.overlayScaleX})
                                        scaleY(${attributes.overlayScaleY})
                                        translateX(${attributes.overlayTranslateX}${attributes.overlayTranslateXUnit})
                                        translateY(${attributes.overlayTranslateY}${attributes.overlayTranslateYUnit})
                                    `,
                                }}
                            />
                        )}
                        {attributes.overlayImageTablet && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPositionTablet} tablet-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImageTablet})`,
                                    height: `${attributes.overlaySizeTablet}${attributes.overlaySizeUnitTablet}`,
                                    opacity: attributes.overlayOpacityTablet,
                                    backgroundSize:
                                        attributes.overlayBackgroundSizeTablet,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPositionTablet,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeatTablet,
                                    zIndex: attributes.overlayZIndexTablet,
                                    mixBlendMode:
                                        attributes.overlayBlendModeTablet,
                                    transform: `
                                        rotate(${attributes.overlayRotateTablet}deg)
                                        scaleX(${attributes.overlayScaleXTablet})
                                        scaleY(${attributes.overlayScaleYTablet})
                                        translateX(${attributes.overlayTranslateXTablet}${attributes.overlayTranslateXUnitTablet})
                                        translateY(${attributes.overlayTranslateYTablet}${attributes.overlayTranslateYUnitTablet})
                                    `,
                                }}
                            />
                        )}
                        {attributes.overlayImageMobile && (
                            <div
                                className={`slider-overlay slider-overlay-${attributes.overlayPositionMobile} mobile-only`}
                                style={{
                                    backgroundImage: `url(${attributes.overlayImageMobile})`,
                                    height: `${attributes.overlaySizeMobile}${attributes.overlaySizeUnitMobile}`,
                                    opacity: attributes.overlayOpacityMobile,
                                    backgroundSize:
                                        attributes.overlayBackgroundSizeMobile,
                                    backgroundPosition:
                                        attributes.overlayBackgroundPositionMobile,
                                    backgroundRepeat:
                                        attributes.overlayBackgroundRepeatMobile,
                                    zIndex: attributes.overlayZIndexMobile,
                                    mixBlendMode:
                                        attributes.overlayBlendModeMobile,
                                    transform: `
                                        rotate(${attributes.overlayRotateMobile}deg)
                                        scaleX(${attributes.overlayScaleXMobile})
                                        scaleY(${attributes.overlayScaleYMobile})
                                        translateX(${attributes.overlayTranslateXMobile}${attributes.overlayTranslateXUnitMobile})
                                        translateY(${attributes.overlayTranslateYMobile}${attributes.overlayTranslateYUnitMobile})
                                    `,
                                }}
                            />
                        )}
                    </>
                )}
                <div className="swiper-wrapper">
                    <InnerBlocks.Content />
                </div>
                <div
                    className={`swiper-navigation swiper-navigation-${attributes.blockId} swiper-navigation-style-${attributes.navigationStyle}`}
                    style={{
                        "--navigation-size-desktop": `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                        "--navigation-size-tablet": `${attributes.navigationSizeTablet.value}${attributes.navigationSizeTablet.valueUnit}`,
                        "--navigation-size-mobile": `${attributes.navigationSizeMobile.value}${attributes.navigationSizeMobile.valueUnit}`,
                    }}
                >
                    <div
                        className={`swiper-button-prev swiper-button-prev-${attributes.blockId}`}
                        style={{
                            visibility:
                                attributes.showNavigation ||
                                attributes.showNavigationTablet ||
                                attributes.showNavigationMobile
                                    ? "visible"
                                    : "hidden",
                            width: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            height: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            backgroundColor:
                                attributes.navigationBackgroundColor,
                            borderColor: attributes.navigationBorderColor,
                            transform: `translateX(${attributes.navigationArrowOffsetLeft}${attributes.navigationArrowOffsetLeftUnit})`,
                            "--swiper-navigation-color":
                                attributes.navigationArrowColor,
                        }}
                    ></div>
                    <div
                        className={`swiper-button-next swiper-button-next-${attributes.blockId}`}
                        style={{
                            visibility:
                                attributes.showNavigation ||
                                attributes.showNavigationTablet ||
                                attributes.showNavigationMobile
                                    ? "visible"
                                    : "hidden",
                            width: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            height: `${attributes.navigationSizeDesktop.value}${attributes.navigationSizeDesktop.valueUnit}`,
                            backgroundColor:
                                attributes.navigationBackgroundColor,
                            borderColor: attributes.navigationBorderColor,
                            transform: `translateX(${attributes.navigationArrowOffsetRight}${attributes.navigationArrowOffsetRightUnit})`,
                            "--swiper-navigation-color":
                                attributes.navigationArrowColor,
                        }}
                    ></div>
                </div>
                <div
                    className={`swiper-pagination swiper-pagination-${attributes.blockId} swiper-pagination-style-${attributes.paginationStyle} swiper-pagination-position-${attributes.paginationPosition}`}
                    data-pagination-style={attributes.paginationStyle}
                    style={{
                        visibility:
                            attributes.showPagination ||
                            attributes.showPaginationTablet ||
                            attributes.showPaginationMobile
                                ? "visible"
                                : "hidden",
                        transform: `translateX(${attributes.paginationOffset}${attributes.paginationUnit})`,
                    }}
                ></div>
            </div>
        );
    },
});

registerBlockType("kamikaze/slide-block", {
    apiVersion: 2,
    title: "Slajd",
    icon: "slides",
    category: "design",
    parent: ["kamikaze/slider-block"],
    supports: {
        html: false,
        reusable: false,
    },
    attributes: {
        showTitle: {
            type: "boolean",
            default: true,
        },
        showExcerpt: {
            type: "boolean",
            default: true,
        },
        postId: {
            type: "number",
        },
    },
    edit: function SlideEdit({ attributes, setAttributes, clientId }) {
        const blockProps = useBlockProps({
            className: "swiper-slide",
        });

        return attributes.postId ? (
            <BlockContextProvider
                value={{
                    postId: attributes.postId,
                    postType: "post",
                }}
            >
                <div {...blockProps}>
                    <InnerBlocks
                        template={[
                            ["core/post-featured-image"],
                            ["core/post-title"],
                            ["core/post-excerpt"],
                        ]}
                        templateLock={false}
                        renderAppender={() => (
                            <InnerBlocks.ButtonBlockAppender />
                        )}
                    />
                </div>
            </BlockContextProvider>
        ) : (
            <div {...blockProps}>
                <InnerBlocks
                    templateLock={false}
                    renderAppender={() => <InnerBlocks.ButtonBlockAppender />}
                />
            </div>
        );
    },
    save: function SlidesSave({ attributes }) {
        const blockProps = useBlockProps.save({
            className: "swiper-slide",
            "data-post-id": attributes.postId || null,
        });

        if (attributes.postId) {
            return (
                <div {...blockProps}>
                    <div
                        className="wp-block-post"
                        data-post-id={attributes.postId}
                    >
                        <InnerBlocks.Content />
                    </div>
                </div>
            );
        }

        return (
            <div {...blockProps}>
                <InnerBlocks.Content />
            </div>
        );
    },
});
